'use client'
import { Breadcrumbs as WebUIBreadcrumbs } from '@lastbrand/web-ui/breadcrumbs'
import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import TrackedLink from '@src/quince-components/src/atoms/link'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import getB2BRoute from '@src/quince-site-common/src/utils/b2b/get-b2b-route'

import type { BreadcrumbsProps } from './breadcrumbs-ds.types'
import type { RenderLink } from '@lastbrand/web-ui/breadcrumbs'

export function BreadcrumbsDS({
  directory,
  showLoader,
  deviceType,
  ariaLabel: ariaLabelProp,
  css: cssProp,
}: BreadcrumbsProps) {
  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()

  const ariaLabel = ariaLabelProp ?? t('breadcrumbs_aria', { defaultValue: 'Breadcrumbs' })

  const items = useMemo(() => {
    const paths = directory.paths
    return paths.map((path, index) => {
      const isLast = index === paths.length - 1
      const href = isLast ? undefined : isB2BMode ? getB2BRoute(path.route) : path.route
      return {
        label: path.displayName,
        href,
      }
    })
  }, [directory.paths, isB2BMode])

  const renderLink: RenderLink = useCallback(({ href, label, className }) => {
    return (
      <TrackedLink
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- RenderLink params are `any` when web-ui subpath can't be resolved by the eslint resolver
        href={href}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- RenderLink params are `any` when web-ui subpath can't be resolved by the eslint resolver
        className={className}
        withoutBaseStyle
        hidePointerCursor
        trackConfig={{
          trackInternal: true,
          category: 'navigation',
          // @i18n-ignore
          action: 'click_bread_crumb',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- label/href are `any` when web-ui subpath can't be resolved by the eslint resolver
          label,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- label/href are `any` when web-ui subpath can't be resolved by the eslint resolver
          value: href,
        }}
      >
        {label}
      </TrackedLink>
    )
  }, [])

  const rootClass = useMemo(
    () =>
      // Figma breadcrumbs (`2810:23757`) align start on both desktop and
      // mobile (matches the inner content frame's `items-start`). We keep the
      // legacy `text-transform: capitalize` on `nav a / nav span` because the
      // current breadcrumb labels come pre-formatted lowercased from the API
      // and rely on this rule for the visible casing; revisit when labels are
      // sent in their final display casing.
      css({
        '& nav a, & nav span': {
          // eslint-disable-next-line i18next/no-literal-string -- CSS keyword, not user-facing copy
          textTransform: 'capitalize',
        },
        // eslint-disable-next-line i18next/no-literal-string -- Panda layout tokens (not user-facing copy)
        display: 'flex',
        justifyContent: { base: 'center', lg: 'flex-start' },
        alignItems: 'center',
        // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
        mx: { base: 'space-4', lg: 0 },
      }),
    [],
  )

  const navCss = useMemo(
    () =>
      css.raw(
        // eslint-disable-next-line i18next/no-literal-string -- Panda text style token (not user-facing copy)
        deviceType === DEVICE_TYPES.DESKTOP
          ? {
              // eslint-disable-next-line i18next/no-literal-string -- Panda text style token (not user-facing copy)
              textStyle: 'body-14',
            }
          : undefined,
        cssProp,
      ),
    [cssProp, deviceType],
  )

  if (showLoader) {
    const loaderCss = {
      // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
      h: 'space-16',
      w: {
        // eslint-disable-next-line i18next/no-literal-string -- Panda width token (not user-facing copy)
        base: 'full',
        // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token (not user-facing copy)
        md: 'space-120',
      },
    }

    return <Skeleton aria-hidden css={loaderCss} />
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- WebUIBreadcrumbs is typed as error when web-ui subpath can't be resolved by the eslint resolver
  return (
    <div className={rootClass}>
      <WebUIBreadcrumbs items={items} renderLink={renderLink} css={navCss} ariaLabel={ariaLabel} />
    </div>
  )
}

export type { BreadcrumbsProps } from './breadcrumbs-ds.types'
